import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  IDiscussionRepliesResponse,
  IDiscussionRootsResponse,
  IPostDiscussionResponse,
} from '../../interface/homeworkDiscussion.interface';
import { IErrorResponse } from '../../interface/main.interface';
import { homeworkService } from '../../services/homework';

/**
 * Shared catch handler. Mirrors the `homework.action.ts` try/catch shape
 * (`const error = err as AxiosError<IErrorResponse>; if (!error.response) throw err;
 * return rejectWithValue(error.response.data);`). The api interceptor rejects with
 * the response *body* (not an AxiosError), so `error.response` is undefined and the
 * 4xx body arrives as `err` directly — we forward that body (which carries `status`)
 * to `rejectWithValue` so the slice can detect a 403 (non-participant -> forbidden).
 */
const rejectWithBody = (err: unknown, rejectWithValue: (value: IErrorResponse) => unknown) => {
  const error = err as AxiosError<IErrorResponse>;
  if (error.response) {
    return rejectWithValue(error.response.data as IErrorResponse);
  }
  const body = err as IErrorResponse;
  if (body && typeof body === 'object' && ('status' in body || 'message' in body)) {
    return rejectWithValue(body);
  }
  throw err;
};

// Roots list. Pagination omitted -> ALL roots (each with replyCount + latestReply).
export const fetchDiscussionRoots = createAsyncThunk<
  IDiscussionRootsResponse,
  string,
  { rejectValue: IErrorResponse }
>('homeworkDiscussion/fetch-roots', async (homeworkId, { rejectWithValue }) => {
  try {
    const response = await homeworkService.fetchHomeworkDiscussion(homeworkId);
    return response.data;
  } catch (err) {
    return rejectWithBody(err, rejectWithValue) as never;
  }
});

// Replies of one root, loaded lazily (page optional -> all replies).
export const fetchDiscussionReplies = createAsyncThunk<
  IDiscussionRepliesResponse,
  { homeworkId: string; rootId: string; page?: number; pageSize?: number },
  { rejectValue: IErrorResponse }
>(
  'homeworkDiscussion/fetch-replies',
  async ({ homeworkId, rootId, page, pageSize }, { rejectWithValue }) => {
    try {
      const response = await homeworkService.fetchDiscussionReplies(homeworkId, rootId, {
        page,
        pageSize,
      });
      return response.data;
    } catch (err) {
      return rejectWithBody(err, rejectWithValue) as never;
    }
  },
);

// Post a root message (no parentId) or a reply (parentId = a root's id).
export const postDiscussionMessage = createAsyncThunk<
  IPostDiscussionResponse,
  { homeworkId: string; message: string; parentId?: string },
  { rejectValue: IErrorResponse }
>(
  'homeworkDiscussion/post-message',
  async ({ homeworkId, message, parentId }, { rejectWithValue }) => {
    try {
      const response = await homeworkService.postHomeworkDiscussion(homeworkId, {
        message,
        parentId,
      });
      return response.data;
    } catch (err) {
      return rejectWithBody(err, rejectWithValue) as never;
    }
  },
);

export const homeworkDiscussionActions = {
  fetchDiscussionRoots,
  fetchDiscussionReplies,
  postDiscussionMessage,
};
